import { Component } from 'react'

import classNames from 'classnames'
import isEmpty from 'lodash/isEmpty'
import PropTypes from 'prop-types'

import { filterOrdersBySelectedStyleColors } from 'utils/transformations/orders'

import ActionBar from 'components/Core/ActionBar/ActionBar'
import Button from 'components/Core/Button/Button'
import Grid from 'components/Core/Grid/Grid'
import Header from 'components/Core/Header/Header'
import Loader from 'components/Core/Loader/Loader'
import SubHeader from 'components/Core/SubHeader/SubHeader'
import { ORDER_LIST_PAGE } from 'pages/OrderListPage/constants'

import messages from './RemoveStyleColorsWithoutQuantities.messages'
import styles from './RemoveStyleColorsWithoutQuantities.module.css'
import RemoveStyleColorsWithoutQuantitiesList from './RemoveStyleColorsWithoutQuantitiesList/RemoveStyleColorsWithoutQuantitiesList.component'

class RemoveStyleColorsWithoutQuantities extends Component {
  state = {
    review: false,
    loadingMutation: false,
    orders: this.props.orders || [],
  }

  componentDidMount() {
    const { orders, toggleAllStyleColors } = this.props
    if (orders.length > 0) {
      toggleAllStyleColors(orders)
    }
  }

  componentDidUpdate = ({ ordersQueryLoading: prevOrdersQueryLoading }) => {
    const { ordersQueryLoading, toggleAllStyleColors, orders } = this.props

    if (prevOrdersQueryLoading && !ordersQueryLoading) {
      this.setState(
        {
          orders,
        },
        () => toggleAllStyleColors(orders),
      )
    }
  }

  handleLeftButton = () => {
    const { review } = this.state
    const {
      navigate,
      onChangeCurrentPage,
      referral,
      resetSelectedOrdersOrderListPage,
      trackBackToOrdersClick,
      trackPreviousPageClick,
    } = this.props

    if (review) {
      trackPreviousPageClick()
      this.setState({ review: false })
      return
    }

    trackBackToOrdersClick()

    if (referral) {
      resetSelectedOrdersOrderListPage()
      navigate(referral)
      return
    }

    onChangeCurrentPage(ORDER_LIST_PAGE)
  }

  handleRightButtom = () => {
    const { review } = this.state
    const {
      onChangeCurrentPage,
      client: { resetStore },
      resetSelectedStyleColors,
      renderSuccessNotification,
      countSelectedStyleColors,
      countUnselectedStyleColors,
      resetSelectedOrdersOrderListPage,
      trackSubmitClicked,
      trackReviewClicked,
      navigate,
      referral,
      renderToasters,
    } = this.props

    if (review) {
      this.setState(
        {
          loadingMutation: true,
        },
        () => {
          trackSubmitClicked()
          this.removeStyleColors()
            .then(() => {
              resetStore().then(() => {
                resetSelectedOrdersOrderListPage()
                resetSelectedStyleColors()

                if (referral) {
                  renderToasters.renderSuccessToast({
                    description: 'Empty styles successfully removed',
                  })
                  navigate(referral)
                  return
                }

                renderSuccessNotification(
                  messages.successNotification(
                    countSelectedStyleColors,
                    countUnselectedStyleColors,
                  ),
                )

                onChangeCurrentPage(ORDER_LIST_PAGE)
              })
            })
            .catch(() => {
              this.setState({
                loadingMutation: false,
              })
            })
        },
      )
    } else {
      trackReviewClicked()
      this.setState({ review: true })
    }
  }

  removeStyleColors = () => {
    const { handleCancelStyleColorsFromOrder, selectedStyleColors } = this.props
    const orderIdsWithStyleColors = Object.keys(selectedStyleColors).filter(
      (orderId) => !isEmpty(selectedStyleColors[orderId]),
    )
    const mutationPromises = orderIdsWithStyleColors.map((orderId) => {
      const styleColorsIds = Object.keys(selectedStyleColors[orderId])
      return handleCancelStyleColorsFromOrder(orderId, styleColorsIds)
    })
    return Promise.all(mutationPromises)
  }

  renderTotalCount = (orders) => {
    const { countSelectedStyleColors } = this.props
    const totalCount = orders.length

    return (
      <div className={styles.total}>
        {messages.countSelectedStyleColorsSelected(
          countSelectedStyleColors,
          totalCount,
        )}
      </div>
    )
  }

  render() {
    const {
      ordersQueryLoading,
      isStyleColorSelected,
      toggleStyleColor,
      countSelectedStyleColors,
      selectedStyleColors,
    } = this.props

    const { review, loadingMutation, orders } = this.state

    const filteredOrders = review
      ? filterOrdersBySelectedStyleColors(orders, selectedStyleColors)
      : orders

    return (
      <>
        <Grid container>
          <Grid.Row>
            <Grid.Col sm={12} md={12} lg={16}>
              <Header divider>
                {review
                  ? messages.reviewPageTitle
                  : messages.removeStyleColorsTitle}
              </Header>
            </Grid.Col>
          </Grid.Row>
          <Grid.Row>
            <Grid.Col sm={12} md={12} lg={16}>
              <SubHeader>
                <SubHeader.Subtitle>
                  {review
                    ? messages.reviewPageSubTitle
                    : messages.removeStyleColorsSubTitle}
                </SubHeader.Subtitle>
              </SubHeader>
            </Grid.Col>
          </Grid.Row>
          <Grid.Row>
            <Loader
              active={ordersQueryLoading || loadingMutation}
              className={classNames({ [styles.loading]: ordersQueryLoading })}
            >
              <Grid.Col>
                <RemoveStyleColorsWithoutQuantitiesList
                  orders={filteredOrders}
                  isStyleColorSelected={isStyleColorSelected}
                  toggleStyleColor={toggleStyleColor}
                  review={review}
                />
              </Grid.Col>
            </Loader>
          </Grid.Row>
        </Grid>
        <ActionBar editMode>
          <Grid>
            <Grid.Row>
              <Button secondary withGrid onClick={this.handleLeftButton}>
                {review ? messages.previousPage : messages.returnToOrders}
              </Button>
              <Button
                primary
                withGrid
                disabled={countSelectedStyleColors <= 0 || ordersQueryLoading}
                onClick={this.handleRightButtom}
              >
                {review ? messages.submit : messages.review}
              </Button>
              <Grid.Col sm={2} md={2} lg={2} />
              <Grid.Col textAlign="right" sm={6} md={6} lg={10}>
                <div className={styles.paginationContainer}>
                  {!ordersQueryLoading && this.renderTotalCount(filteredOrders)}
                </div>
              </Grid.Col>
            </Grid.Row>
          </Grid>
        </ActionBar>
      </>
    )
  }
}

RemoveStyleColorsWithoutQuantities.propTypes = {
  onChangeCurrentPage: PropTypes.func.isRequired,
  orders: PropTypes.array,
  ordersQueryLoading: PropTypes.bool,
  toggleAllStyleColors: PropTypes.func.isRequired,
  isStyleColorSelected: PropTypes.func,
  toggleStyleColor: PropTypes.func,
  countSelectedStyleColors: PropTypes.number,
  selectedStyleColors: PropTypes.object.isRequired,
  handleCancelStyleColorsFromOrder: PropTypes.func.isRequired,
  client: PropTypes.shape({
    resetStore: PropTypes.func.isRequired,
  }).isRequired,
  resetSelectedStyleColors: PropTypes.func.isRequired,
  countUnselectedStyleColors: PropTypes.number.isRequired,
  renderSuccessNotification: PropTypes.func.isRequired,
  resetSelectedOrdersOrderListPage: PropTypes.func.isRequired,
  trackSubmitClicked: PropTypes.func,
  trackReviewClicked: PropTypes.func,
  trackBackToOrdersClick: PropTypes.func,
  trackPreviousPageClick: PropTypes.func,
}

RemoveStyleColorsWithoutQuantities.defaultProps = {
  ordersQueryLoading: false,
  isStyleColorSelected: () => {},
  toggleStyleColor: () => {},
  countSelectedStyleColors: 0,
  orders: [],
  trackSubmitClicked: () => {},
  trackReviewClicked: () => {},
  trackBackToOrdersClick: () => {},
  trackPreviousPageClick: () => {},
}

export default RemoveStyleColorsWithoutQuantities
